import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PermissionKey, SessionUser } from '@agency/contracts';
import { ApiClientError, get, post, setCsrf } from './api.ts';

type Me = { user: SessionUser; csrfToken: string };
type AuthState = {
  user: SessionUser | null;
  loading: boolean;
  error: ApiClientError | null;
  can: (p: PermissionKey) => boolean;
  signedIn: (me: Me) => void;
  signOut: () => Promise<void>;
  refetch: () => void;
};
const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery<Me | null, ApiClientError>({
    queryKey: ['me'],
    retry: false,
    queryFn: async () => {
      try {
        const me = await get<Me>('/auth/me');
        setCsrf(me.csrfToken);
        return me;
      } catch (e) {
        if (e instanceof ApiClientError && e.status === 401) return null;
        throw e;
      }
    },
  });
  const user = q.data?.user ?? null;
  const value: AuthState = {
    user,
    loading: q.isLoading,
    error: q.error ?? null,
    can: (p) => !!user?.permissions.includes(p),
    signedIn: (me) => { setCsrf(me.csrfToken); qc.setQueryData(['me'], me); },
    signOut: async () => {
      await post('/auth/logout').catch(() => undefined);
      qc.setQueryData(['me'], null);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
    },
    refetch: () => void q.refetch(),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}
